import React, { useState } from "react";
import { Slider } from "@/components/ui/slider";
import { UploadZone, DownloadButton, ResetButton, humanBytes, loadImage } from "./_shared";

export default function ImageCompressor() {
  const [file, setFile] = useState(null);
  const [quality, setQuality] = useState(0.7);
  const [preview, setPreview] = useState(null);
  const [outUrl, setOutUrl] = useState(null);
  const [outSize, setOutSize] = useState(0);
  const [busy, setBusy] = useState(false);

  const process = async (f, q = quality) => {
    setBusy(true);
    const { img } = await loadImage(f);
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext("2d").drawImage(img, 0, 0);
    canvas.toBlob((blob) => {
      if (outUrl) URL.revokeObjectURL(outUrl);
      const url = URL.createObjectURL(blob);
      setOutUrl(url); setOutSize(blob.size); setBusy(false);
    }, "image/jpeg", q);
  };

  const handleFile = async (f) => {
    setFile(f); setPreview(URL.createObjectURL(f));
    await process(f, quality);
  };

  const reset = () => { setFile(null); setPreview(null); setOutUrl(null); setOutSize(0); };
  const ratio = file && outSize ? Math.round((1 - outSize / file.size) * 100) : 0;

  if (!file) return <UploadZone onFile={handleFile} />;

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-200 dark:border-white/10 overflow-hidden bg-slate-50 dark:bg-[#0b0c0e]">
          <div className="p-3 text-xs font-medium text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-white/10">Original — {humanBytes(file.size)}</div>
          <img src={preview} alt="original" className="w-full max-h-80 object-contain" />
        </div>
        <div className="rounded-2xl border border-slate-200 dark:border-white/10 overflow-hidden bg-slate-50 dark:bg-[#0b0c0e]">
          <div className="p-3 text-xs font-medium text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-white/10">
            Compressed — {humanBytes(outSize)} {ratio > 0 && <span className="text-emerald-600 dark:text-emerald-400">(-{ratio}%)</span>}
          </div>
          {outUrl && <img src={outUrl} alt="out" className="w-full max-h-80 object-contain" />}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 dark:border-white/10 p-6 bg-white dark:bg-[#121316]">
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Quality</label>
          <span className="text-sm text-slate-500 dark:text-slate-400">{Math.round(quality * 100)}%</span>
        </div>
        <Slider
          value={[quality * 100]}
          min={10} max={100} step={5}
          onValueChange={(v) => setQuality(v[0] / 100)}
          onValueCommit={() => file && process(file, quality)}
          data-testid="quality-slider"
        />
        <div className="mt-6 flex items-center gap-3">
          <DownloadButton href={outUrl} filename={`compressed-${file.name.replace(/\.[^.]+$/, "")}.jpg`}>
            {busy ? "Processing…" : "Download Compressed"}
          </DownloadButton>
          <ResetButton onClick={reset} />
        </div>
      </div>
    </div>
  );
}
